import { useCallback, useEffect, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { useDispatch } from 'react-redux'
import { styled } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { PageArea } from '..'
import Loading from '../../components/process-state-notifications/Loading'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
import { useRoleGridOptions } from './gridOptions'
import { useRoleData } from './hooks/useRoleData'
import { useContextMenu } from './hooks/useContextMenu'
import { RoleRow } from './model/RoleRow'
import { requireSave, doNotRequireSave } from '../../../store/requiredSaveData'
import {
  CellValueChangedEvent,
  GridApi,
  GridReadyEvent,
  RowDragEvent,
} from 'ag-grid-community'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import SaveButtonArea from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../containers/BulkSheetView/components/header/ColumnSettingButton'
import { Button } from '../../components/buttons'
import {
  InputError,
  validateNodes,
} from '../../containers/BulkSheetView/lib/validation'
import { getRowNumber } from '../../containers/BulkSheetView/lib/gridApi'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { intl } from '../../../i18n'
import _ from 'lodash'
import { usePermissionPolicyOptions } from '../../hooks/usePermissionPolicyOptions'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useDragStyle } from '../../containers/BulkSheetView/hooks/gridEvents/rowDrag'
import { PermissionPolicyOption } from '../../../domain/value-object/PermissionPolicy'
import { useGridContext } from '../../containers/BulkSheetView/hooks/useGridContext'

type Props = {
  uuid: string
}

const Roles = ({ uuid: functionUuid }: Props) => {
  const {
    initialized: permissionPolicyOptionsInitialized,
    allPermissionPolicyOptions,
    attachedByDefaultPermissionPolicies,
    attachablePermissionPolicyOptions,
  } = usePermissionPolicyOptions()

  if (!permissionPolicyOptionsInitialized) {
    return <></>
  }

  return (
    <Page
      functionUuid={functionUuid}
      allPermissionPolicyOptions={allPermissionPolicyOptions}
      attachedByDefaultPermissionPolicies={attachedByDefaultPermissionPolicies}
      attachablePermissionPolicyOptions={attachablePermissionPolicyOptions}
    />
  )
}

const Page = ({
  functionUuid,
  allPermissionPolicyOptions,
  attachedByDefaultPermissionPolicies,
  attachablePermissionPolicyOptions,
}: {
  functionUuid: string
  allPermissionPolicyOptions: PermissionPolicyOption[]
  attachedByDefaultPermissionPolicies: PermissionPolicyOption[]
  attachablePermissionPolicyOptions: PermissionPolicyOption[]
}) => {
  const dispatch = useDispatch()
  const gridRef = useRef<AgGridReact<RoleRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTargetUuids, setDeleteTargetUuids] = useState<string[]>([])

  const {
    data,
    loading,
    hasChanged,
    fetchRoles,
    saveRoles,
    addRow,
    deleteRows,
    setDataAfterEditing,
  } = useRoleData(functionUuid)

  const gridOptions = useRoleGridOptions(
    attachablePermissionPolicyOptions,
    allPermissionPolicyOptions
  )
  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)
  const { contextRef, updateContext } = useGridContext(
    gridApiRef.current,
    gridOptions.context
  )
  const bulkSheetState = useBulkSheetState(
    functionUuid,
    UiStateKey.RoleColumnAndFilterState
  )
  const columnState = useColumnState({
    gridApi: gridApiRef.current,
    autoGroupColumnDef: gridOptions.autoGroupColumnDef,
    bulkSheetState,
  })
  const columnSetting = useColumnSetting()

  const handleReload = useCallback(() => {
    if (hasChanged()) {
      setCancelDialogOpen(true)
    } else {
      updateContext({
        errors: new InputError(),
      })
      fetchRoles()
    }
  }, [hasChanged, fetchRoles, updateContext])

  const validateBeforeSubmit = useCallback(
    (rows: RoleRow[]) => {
      const targetRowUuids = rows
        .filter(row => row.added || row.edited)
        .map(row => row.uuid)
      const errors = validateNodes({
        gridApi: gridApiRef.current,
        uuids: targetRowUuids,
        extraValidate: (colId, node, colDef) => {
          if (!node || !colId) return
          const validate = colDef.cellRendererParams?.validate
          if (!validate || !gridApiRef.current) return
          const val = gridApiRef.current.getValue(colId, node)
          return validate(val, {
            node,
            data: node.data,
            colDef,
            colId,
            api: gridApiRef.current,
          })
        },
      })
      updateContext({
        errors,
      })
      return errors
    },
    [updateContext]
  )

  const handleSave = useCallback(async () => {
    gridApiRef.current?.stopEditing()

    const errors = validateBeforeSubmit(data)
    if (errors.hasMessage()) {
      dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'global.warning.businessError' }),
          text: errors.toMessage(id => {
            const node = gridApiRef.current?.getRowNode(id)
            return !!node ? getRowNumber(node).toString() : ''
          }),
        })
      )
      return
    }

    await saveRoles()
    dispatch(doNotRequireSave())
  }, [saveRoles, dispatch, validateBeforeSubmit, data])

  const handleAddRow = useCallback(() => {
    addRow(attachedByDefaultPermissionPolicies)
    dispatch(requireSave())
  }, [addRow, dispatch, attachedByDefaultPermissionPolicies])

  const handleDeleteRequest = useCallback((uuids: string[]) => {
    setDeleteTargetUuids(uuids)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    deleteRows(deleteTargetUuids)
    setDeleteDialogOpen(false)
    setDeleteTargetUuids([])
    dispatch(requireSave())
  }, [deleteTargetUuids, deleteRows, dispatch])

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
    setDeleteTargetUuids([])
  }, [])

  const handleCancelConfirm = useCallback(() => {
    setCancelDialogOpen(false)
    fetchRoles()
  }, [fetchRoles])

  const handleCancelClose = useCallback(() => {
    setCancelDialogOpen(false)
  }, [])

  const { getContextMenuItems } = useContextMenu({
    addRowAction: handleAddRow,
    deleteRowAction: handleDeleteRequest,
  })

  const dragStyle = useDragStyle(gridApiRef.current, updateContext)

  const onRowDragEnd = useCallback(
    (event: RowDragEvent<RoleRow>) => {
      const movedIds = event.nodes.map(v => v.id)
      const overNode = event.overNode
      try {
        if (!overNode || movedIds.includes(overNode.id)) {
          return
        }
        const result = data.filter(v => !movedIds.includes(v.uuid))
        const moved = data.filter(v => movedIds.includes(v.uuid))
        // If moved toward down, move after overNode.
        const index =
          result.findIndex(v => v.uuid === overNode.id!) +
          (event.overIndex > (event.node.rowIndex || 0) ? 1 : 0)
        result.splice(index, 0, ...moved)
        setDataAfterEditing(result)
      } finally {
        // Clear drag cell style
        dragStyle.refreshDragStyle()
      }
    },
    [data, dragStyle, setDataAfterEditing]
  )

  const onRowDataUpdated = useCallback((event: any) => {
    event.api?.refreshCells({ columns: ['rowNumber'], force: true })
  }, [])

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      gridApiRef.current = e.api
      setIsGridReady(true)
      if (!_.isEmpty(bulkSheetState.columnState)) {
        e.api.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
    },
    [bulkSheetState.columnState]
  )

  const onFirstDataRendered = useCallback(() => {
    if (!gridApiRef.current) return

    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridApiRef.current.setFilterModel(bulkSheetState.filterState ?? null)
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState.filterState, bulkSheetState.finishRestoring])

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<RoleRow>) => {
      if (event.colDef.field === 'permissionPolicyUuids') {
        event.api.refreshCells({
          rowNodes: [event.node],
          columns: ['availableFunctions'],
          force: true,
        })
      }
    },
    []
  )

  useEffect(() => {
    if (!bulkSheetState.initialized) return
    fetchRoles()
  }, [bulkSheetState.initialized, fetchRoles])

  useEffect(() => {
    if (hasChanged()) {
      dispatch(requireSave())
    }
  }, [data, hasChanged, dispatch])

  const getDeleteRowLabels = useCallback(() => {
    return deleteTargetUuids
      .map(uuid => {
        const row = data.find(r => r.uuid === uuid)
        return row?.name || ''
      })
      .filter(name => name !== '')
  }, [deleteTargetUuids, data])

  useKeyBind(
    [{ key: KEY_SAVE, fn: handleSave, stopDefaultBehavior: true }],
    [handleSave]
  )

  if (!bulkSheetState.initialized) {
    return <></>
  }

  return (
    <PageArea>
      <Header>
        <SaveButtonArea loading={loading} onSubmit={handleSave} />
        <ReloadButton onReload={handleReload} />
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
        <ColumnSettingButton
          onClick={columnSetting.toggle}
          open={columnSetting.isOpen}
        />
        <Button
          variant="filled"
          colorPattern="skyBlue"
          size="s"
          onClick={handleAddRow}
          style={{ marginLeft: '10px' }}
        >
          <AddRoundedIcon fontSize="small" />
          {intl.formatMessage({
            id: 'roles.header.addButton',
          })}
        </Button>
      </Header>
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={data}
        getContextMenuItems={getContextMenuItems}
        onGridReady={onGridReady}
        onRowDataUpdated={onRowDataUpdated}
        onFirstDataRendered={onFirstDataRendered}
        onColumnVisible={columnState.onColumnVisible}
        onColumnResized={columnState.rememberColumnState}
        onColumnMoved={columnState.rememberColumnState}
        onFilterChanged={columnState.onFilterChanged}
        onSortChanged={columnState.onSortChanged}
        onRowDragEnter={dragStyle.onRowDragEnter}
        onRowDragMove={dragStyle.onRowDragMove}
        onRowDragEnd={onRowDragEnd}
        onRowDragLeave={dragStyle.refreshDragStyle}
        onCellValueChanged={onCellValueChanged}
      />
      <Loading isLoading={loading} elem={bulkSheetRef.current} />
      <CancelConfirmDialog
        open={cancelDialogOpen}
        onConfirm={handleCancelConfirm}
        onClose={handleCancelClose}
      />
      {deleteDialogOpen && (
        <DeleteRowConfirmationDialog
          target={getDeleteRowLabels()}
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteCancel}
        />
      )}
      {columnSetting.isOpen && (
        <ColumnSettingPopper
          anchorEl={columnSetting.anchorEl}
          open={columnSetting.isOpen}
          close={columnSetting.close}
          gridApi={gridApiRef.current || undefined}
          height={bulkSheetRef.current?.offsetHeight}
          openSavedUiStateDialog={undefined}
          initializeColumnState={columnState.resetColumnState}
          applicationFunctionUuid={functionUuid}
          uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
          columnState={bulkSheetState.columnState}
          offset={120}
        />
      )}
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export default Roles
